import type { Directive, DirectiveBinding } from 'vue'

/**
 * v-aos —— AOS 风格的滚动进入动画指令
 *
 * 当元素滚动进入视口时，自动添加 .aos-animate 触发过渡动画。
 * 离开视口后（repeat 模式）会反向隐藏，再次进入时重新播放。
 *
 * 用法：
 *   <div v-aos />                                     默认从下方淡入
 *   <div v-aos.up />  v-aos.down / left / right       方向
 *   <div v-aos.zoom />  v-aos.fade                    缩放 / 纯淡入
 *   <div v-aos="{ delay: 80, duration: 500 }" />      自定义延迟/时长（错位进入）
 *   <div v-aos.repeat />                              滚动离开后再次进入可重复播放
 *
 * 依赖全局样式（styles/aos.scss）提供 [data-aos] 的初始隐藏态与 .aos-animate 的显现态。
 * 表格（el-table）行进入动画由 styles/aos.scss 中的 .el-table__body tr 规则自动提供。
 */

type Direction = 'up' | 'down' | 'left' | 'right' | 'fade' | 'zoom'

interface AosOptions {
  direction?: Direction
  delay?: number
  duration?: number
  distance?: number
  once?: boolean
}

const DIRECTIONS: Direction[] = ['up', 'down', 'left', 'right', 'fade', 'zoom']
const DEFAULT_DURATION = 600
const DEFAULT_DISTANCE = 24

let observer: IntersectionObserver | null = null
const observed = new WeakSet<Element>()

function getObserver(): IntersectionObserver {
  if (observer) return observer
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const el = entry.target as HTMLElement
        const once = el.dataset.aosOnce !== 'false'
        if (entry.isIntersecting) {
          el.classList.add('aos-animate')
          if (once) observer!.unobserve(el)
        } else if (!once) {
          el.classList.remove('aos-animate')
        }
      }
    },
    { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.01 },
  )
  return observer
}

function resolve(binding: DirectiveBinding): { dir: Direction; opts: AosOptions } {
  let dir: Direction = 'up'
  const opts: AosOptions = {}
  for (const d of DIRECTIONS) {
    if (binding.modifiers[d]) {
      dir = d
      break
    }
  }
  const v = binding.value
  if (typeof v === 'string' && DIRECTIONS.includes(v as Direction)) {
    dir = v as Direction
  } else if (v && typeof v === 'object') {
    Object.assign(opts, v)
    if (opts.direction) dir = opts.direction
  }
  if (opts.once === undefined) opts.once = !binding.modifiers.repeat
  return { dir, opts }
}

function setup(el: HTMLElement, binding: DirectiveBinding) {
  const { dir, opts } = resolve(binding)

  // 尊重系统「减少动态效果」偏好：直接显示，不做动画
  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  if (reduce) {
    el.setAttribute('data-aos', dir)
    el.classList.add('aos-animate')
    return
  }

  el.setAttribute('data-aos', dir)
  el.style.setProperty('--aos-duration', `${(opts.duration ?? DEFAULT_DURATION)}ms`)
  el.style.setProperty('--aos-delay', `${(opts.delay ?? 0)}ms`)
  el.style.setProperty('--aos-distance', `${(opts.distance ?? DEFAULT_DISTANCE)}px`)
  el.dataset.aosOnce = String(opts.once)

  getObserver().observe(el)
  observed.add(el)
}

function cleanup(el: HTMLElement) {
  if (observed.has(el)) {
    observer?.unobserve(el)
    observed.delete(el)
  }
  el.removeAttribute('data-aos')
  el.classList.remove('aos-animate')
  delete el.dataset.aosOnce
}

const aos: Directive<HTMLElement, AosOptions | Direction | string | undefined> = {
  mounted(el, binding) {
    setup(el, binding)
  },
  unmounted(el) {
    cleanup(el)
  },
}

export default aos
